import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { toast } from 'react-toastify';
import { PickUpPoint, ShelfCompartment } from '../types';

type CollectionDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  orderId: number;
  currentFlyers: number | null;
  currentBoxes: number | null;
  currentParcels: number | null;
  currentPickUpPointId: number | null;
  currentPickUpPointLocation: string | null;
  currentShelfCompartmentId: number | null;
};

const CollectionDetailsModal: React.FC<CollectionDetailsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  orderId,
  currentFlyers,
  currentBoxes,
  currentParcels,
  currentPickUpPointId,
  currentPickUpPointLocation,
  currentShelfCompartmentId,
}) => {
  const [flyers, setFlyers] = useState<number | null>(currentFlyers);
  const [boxes, setBoxes] = useState<number | null>(currentBoxes);
  const [parcels, setParcels] = useState<number | null>(currentParcels);
  const [shelfCompartmentId, setShelfCompartmentId] = useState<number | null>(currentShelfCompartmentId);
  const [pickUpPoints, setPickUpPoints] = useState<PickUpPoint[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFlyers(currentFlyers);
      setBoxes(currentBoxes);
      setParcels(currentParcels);
      setShelfCompartmentId(currentShelfCompartmentId);

      fetchWithAuth('/pick_up_points?per_page=100')
        .then(res => res.json())
        .then(data => setPickUpPoints(data.pick_up_points || []))
        .catch(() => toast.error('Failed to load pick-up points'));
    }
  }, [isOpen, currentFlyers, currentBoxes, currentParcels, currentShelfCompartmentId]);

  if (!isOpen) return null;

  const selectedPickUpPoint = pickUpPoints.find(p => p.id === currentPickUpPointId);
  const availableCompartments: ShelfCompartment[] = selectedPickUpPoint?.shelf_compartments ?? [];
  // Sorted by code so the dropdown always reads A1, A2, A3… — the API orders them too,
  // but this keeps the list stable even if a response ever comes back unordered.
  const selectableCompartments = availableCompartments
    .filter(compartment => compartment.order_id === null || compartment.order_id === orderId)
    .sort((a, b) => a.code.localeCompare(b.code));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const res = await fetchWithAuth(`/orders/${orderId}/update_collection_details`, {
        method: 'PATCH',
        body: JSON.stringify({
          flyers,
          boxes,
          parcels,
          shelf_compartment_id: shelfCompartmentId,
        }),
      });

      if (res.ok) {
        toast.success('Collection details updated');
        onSuccess();
        onClose();
      } else {
        const data = await res.json();
        toast.error(data.errors?.[0] || 'Failed to update collection details');
      }
    } catch {
      toast.error('An error occurred while updating collection details.');
    } finally {
      setIsSaving(false);
    }
  };

  const fields: { label: string; value: number | null; setter: (v: number | null) => void }[] = [
    { label: 'Flyers', value: flyers, setter: setFlyers },
    { label: 'Boxes', value: boxes, setter: setBoxes },
    { label: 'Parcels', value: parcels, setter: setParcels },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-md shadow-lg w-full max-w-md flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-bold">Collection Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <div className="p-4 space-y-4">
          {fields.map(({ label, value, setter }) => (
            <div key={label} className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">{label}</span>
              {value != null ? (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setter(Math.max(0, value - 1))}
                    disabled={value <= 0}
                    className="w-7 h-7 rounded border flex items-center justify-center text-lg font-medium disabled:opacity-30 hover:bg-gray-100"
                    aria-label={`Decrease ${label.toLowerCase()}`}
                  >
                    −
                  </button>
                  <span className="w-8 text-center text-sm font-medium tabular-nums">
                    {value}
                  </span>
                  <button
                    onClick={() => setter(value + 1)}
                    className="w-7 h-7 rounded border flex items-center justify-center text-lg font-medium hover:bg-gray-100"
                    aria-label={`Increase ${label.toLowerCase()}`}
                  >
                    +
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setter(0)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Set value
                </button>
              )}
            </div>
          ))}

          {currentPickUpPointLocation && (
            <>
              <hr className="border-gray-200" />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pick-Up Point</label>
                <p className="text-sm text-gray-600">{currentPickUpPointLocation}</p>
              </div>
            </>
          )}

          {currentPickUpPointId && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shelf Compartment</label>
                <select
                  className="w-full px-3 py-2 border rounded"
                  value={shelfCompartmentId ?? ''}
                  onChange={e => setShelfCompartmentId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">— None —</option>
                  {selectableCompartments.map(compartment => (
                    <option key={compartment.id} value={compartment.id}>
                      {compartment.code} ({compartment.size})
                    </option>
                  ))}
                </select>
                {selectableCompartments.length === 0 && (
                  <p className="text-sm text-gray-500 mt-1">No available compartments at this pick-up point</p>
                )}
              </div>
            </>
          )}
        </div>
        <div className="flex justify-end p-4 border-t">
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollectionDetailsModal;
